import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRotateRight } from "@fortawesome/free-solid-svg-icons"
import MainTitle from "../../components/template/MainTitle"
import SubTitle from "../../components/template/SubTitle"
import ButtonWrap from "../../components/template/ButtonWrap"
import { classNames } from "../../components/utils/Utils"
import TrainingReportSummary from "../TrainingReportPage/TrainingReportSummary"
import TrainingReportGraphView from "../TrainingReportPage/TrainingReportGraphView"
import TrainingReportTableView from "../TrainingReportPage/TrainingReportTableView"

const TrainingProgressStatusPill = ({ value }) => (
    <span
        className={classNames(
            "px-3 py-1 uppercase leading-wide font-bold text-xs rounded-full shadow-sm",
            value === 'not_started' ? "bg-gray-100 text-gray-700" : null,
            value === 'in_progress' ? "bg-yellow-100 text-yellow-700" : null,
            value === 'completed' ? "bg-green-100 text-green-700" : null,
        )}
    >{value.replaceAll("_", " ")}</span>
)

// Presentational view for a training progress snapshot; data fetching is up
// to the page wrapping it (lookup key differs per entry point).
const TrainingProgressView = ({ progress, isLoading, error, onRefresh, headerAction = null }) => {
    // Report components read tr_lr; the progress metrics name the same column lr.
    const reportData = (progress?.metrics ?? []).map(({ lr, ...rest }) => ({ ...rest, tr_lr: lr ?? null }))

    const renderContent = () => {
        if (isLoading && !progress) {
            return <div className="my-5">Loading...</div>
        }
        if (error && !progress) {
            return <div className="my-5 text-gray-500">{error}</div>
        }
        if (progress.status === 'not_started' || reportData.length === 0) {
            return <div className="my-5 text-gray-500">Training has not produced any metrics yet. Use the refresh button to check again.</div>
        }
        return (
            <div className="grid gap-4 mt-5">
                <TrainingReportSummary data={reportData} />
                <TrainingReportGraphView data={reportData} />
                <TrainingReportTableView data={reportData} />
            </div>
        )
    }

    return (
        <div className='default-background'>
            <SubTitle title="Training Progress" />
            <div className="flex justify-between items-center my-5">
                {/* The pill group lives inside the title's inline flow so it
                    trails the last word when the model name wraps. */}
                <MainTitle title={<>
                    {progress?.model_name ?? ''}
                    {progress && (
                        <span className="ml-3 inline-flex items-center gap-3 whitespace-nowrap align-middle text-sm font-normal">
                            <TrainingProgressStatusPill value={progress.status} />
                            <span className="text-gray-500">
                                Epoch {progress.current_epoch}{progress.total_epochs != null ? ` / ${progress.total_epochs}` : ''}
                            </span>
                        </span>
                    )}
                </>} />
                <div className="flex items-center gap-2 shrink-0">
                    {headerAction}
                    {progress?.status !== 'completed' && (
                        <ButtonWrap contents={<FontAwesomeIcon icon={faArrowRotateRight} />} onClick={onRefresh} />
                    )}
                </div>
            </div>
            {error && progress && <div className="my-2 text-sm text-red-500">{error}</div>}
            {renderContent()}
        </div>
    )
}

export default TrainingProgressView
